import React, { useEffect, useMemo, useState } from 'react';
import WithNavigation from '../../layouts/WithNavigation';
import { getCalendarEvents, getCalendarMetadata } from 'src/services/client';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { formatApiEventsToCalendarEvents } from 'src/pages/Calendar/helpers';
import {
  EventTypeStyle,
  getEventTypeStyle,
} from 'src/utils/BigCalendarColourGeneratorUtils';
import CalendarContent from 'src/components/CalendarContent/CalendarContent';
import CalendarHeader from 'src/components/CalendarHeader/CalendarHeader';
import {
  ICalendarEvent,
  ICalendarEventData,
  ICalendarEventsServiceResponse,
  ICalendarMetadataApiResult,
  ICalendarMetadataData,
  IProperty,
  IPropertyWithState,
  IUniqueEventType,
  SERVICE_REQUEST_EVENT_TYPE,
  WALKTHROUGH_EVENT_TYPE,
} from 'src/pages/Calendar/types';
import 'react-datepicker/dist/react-datepicker.css';
import './Calendar.scss';

export const CalendarComponent = () => {
  const [availableProperties, setAvailableProperties] = useState<IProperty[]>(
    []
  );
  const [availableEventTypes, setAvailableEventTypes] = useState<number[]>([]);
  const [events, setEvents] = useState<ICalendarEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const [fromDate, setFromDate] = useState<Date>(
    new Date(currentYear, currentMonth, 1)
  ); // First day of current month.
  const [toDate, setToDate] = useState<Date>(
    new Date(currentYear, currentMonth + 1, 0)
  ); // Last day of current month.

  // Set start of day for fromDate and end of day for toDate.
  const minDate: Date = new Date(fromDate);
  minDate.setHours(0, 0, 0, 0);
  const maxDate: Date = new Date(toDate);
  maxDate.setHours(23, 59, 59, 999);
  const today: Date = new Date();
  today.setHours(0, 0, 0, 0); // Reset time part for accurate date comparison.

  const todayFormatted: string = today.toDateString();
  const toDateFormatted: string = toDate.toDateString();
  const isTodaySelected: boolean = todayFormatted === toDateFormatted;

  const goToToday = () => {
    const today = new Date();
    setToDate(today);
  };

  const user: any = useSelector(selectAuthenticatedUser);

  const fetchMetaData = async () => {
    try {
      const response: ICalendarMetadataApiResult = await getCalendarMetadata();

      if (response.ok && response.data) {
        const metadata: ICalendarMetadataData = response.data.data;
        setAvailableProperties(metadata.availableProperties || []);
        setAvailableEventTypes(metadata.availableEventTypes || []);
      } else {
        console.error('Failed to fetch metadata:', response.error);
      }
    } catch (err) {
      console.error('Error fetching calendar metadata:', err);
    }
  };

  const fetchEvents = async () => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Format dates for API (YYYY-MM-DD).
      const formattedFromDate: string = fromDate.toISOString().split('T')[0];
      const formattedToDate: string = toDate.toISOString().split('T')[0];

      const response: ICalendarEventsServiceResponse = await getCalendarEvents(
        formattedFromDate,
        formattedToDate
      );

      const eventsData: ICalendarEventData[] = response?.data?.data;

      if (eventsData && Array.isArray(eventsData)) {
        const formattedEvents: ICalendarEvent[] =
          formatApiEventsToCalendarEvents(eventsData);
        setEvents(formattedEvents);
      }
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      setError('Failed to load calendar events. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [user?.id, fromDate, toDate]);

  useEffect(() => {
    fetchMetaData();
  }, []);

  // We want to combine event types data structure with our selection states.
  const allEventTypesWithStates: IUniqueEventType[] = useMemo(() => {
    return availableEventTypes.map((eventTypeId: number) => {
      const eventTypeKey = eventTypeId === 1 ? 'walkthrough' : 'service';
      const style: EventTypeStyle = getEventTypeStyle(eventTypeKey);

      // Check if this event type appears in any event in current date range.
      const hasEventsInDateRange: boolean = events.some(
        (event: ICalendarEvent) => {
          const eventTypeAsNumber: number =
            typeof event.eventType === 'string'
              ? event.eventType === 'walkthrough'
                ? WALKTHROUGH_EVENT_TYPE
                : SERVICE_REQUEST_EVENT_TYPE
              : event.eventType;
          return eventTypeAsNumber === eventTypeId;
        }
      );

      return {
        text: style.label, // "Walkthrough" or "Service Request".
        value: eventTypeKey, // "walkthrough" or "service".
        color: style.backgroundColor, // Original colors from EVENT_TYPE_STYLES.
        textColor: style.textColor, // Original text colors.
        enabled: hasEventsInDateRange, // true if event type has events, false if not.
      };
    });
  }, [availableEventTypes, events]);

  // Combined properties data structure with selection states
  const allPropertiesWithStates: IPropertyWithState[] = useMemo(() => {
    return availableProperties.map((apiProperty: IProperty) => {
      const hasEventsInDateRange: boolean = events.some(
        (event: ICalendarEvent) =>
          event.originalEvent.propertyId === apiProperty.propertyId
      );

      return {
        id: apiProperty.propertyId,
        text: apiProperty.name,
        enabled: hasEventsInDateRange,
      };
    });
  }, [availableProperties, events]);

  return (
    <WithNavigation
      title="Calendar"
      top={
        <CalendarHeader
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          isTodaySelected={isTodaySelected}
          onGoToToday={goToToday}
          error={error}
        />
      }
      bottom={
        <CalendarContent
          uniqueEventTypes={allEventTypesWithStates}
          allPropertiesWithStates={allPropertiesWithStates}
          loading={loading}
          events={events}
          minDate={minDate}
          maxDate={maxDate}
          defaultDate={
            new Date() >= minDate && new Date() <= maxDate
              ? new Date()
              : minDate
          }
          onSelectEvent={null}
        />
      }
    />
  );
};

export default CalendarComponent;
